/**
 * TransferReportsConfirmStep Container Component
 *
 * Manages the confirmation input state. The "Confirm Transfer" button
 * is only enabled when the user types "transfer" (case-insensitive).
 * @module features/player-detail/components/TransferReportsConfirmStep
 */

import { useCallback, useMemo, useState } from "react";

import TransferReportsConfirmStepView from "./TransferReportsConfirmStepView";

/** Props for TransferReportsConfirmStepContainer */
export interface TransferReportsConfirmStepContainerProps {
  /** Source manual player name */
  readonly sourcePlayerName: string;
  /** Target player display name */
  readonly targetPlayerName: string;
  /** Total number of items to transfer */
  readonly totalItemCount: number;
  /** Callback fired when user confirms the transfer */
  readonly onConfirm: () => void;
  /** Callback fired when user goes back */
  readonly onBack: () => void;
}

/** The keyword the user must type to enable confirmation */
const CONFIRMATION_KEYWORD = "transfer";

/**
 * Container that holds confirmation input state
 * @param root0 - Component props
 * @param root0.sourcePlayerName - Source manual player name
 * @param root0.targetPlayerName - Target player display name
 * @param root0.totalItemCount - Total items to transfer
 * @param root0.onConfirm - Confirm handler
 * @param root0.onBack - Back handler
 */
const TransferReportsConfirmStepContainer = ({
  sourcePlayerName,
  targetPlayerName,
  totalItemCount,
  onConfirm,
  onBack,
}: TransferReportsConfirmStepContainerProps) => {
  // SE-7733: `confirmationText` drives the gate comparison ONLY — it is
  // deliberately never fed back into the field, which is uncontrolled. See the
  // note on the input in the View. Emptiness on re-entry comes from this
  // container being unmounted when the modal leaves the confirm step, so a
  // fresh mount gets a fresh `useState("")` and a fresh native field; no reset
  // effect exists or is needed (and `react-hooks/set-state-in-effect` forbids
  // one).
  const [confirmationText, setConfirmationText] = useState("");

  // Validation is unchanged by SE-7733 and must stay exactly this strict:
  // case-insensitive, surrounding whitespace trimmed, otherwise an exact match.
  // No prefix acceptance, no debounce. Pinned in
  // __tests__/TransferReportsConfirmStepTypedInput.test.tsx in both directions.
  const isConfirmEnabled = useMemo(
    () => confirmationText.toLowerCase().trim() === CONFIRMATION_KEYWORD,
    [confirmationText]
  );

  const handleChangeText = useCallback((text: string) => {
    setConfirmationText(text);
  }, []);

  return (
    <TransferReportsConfirmStepView
      sourcePlayerName={sourcePlayerName}
      targetPlayerName={targetPlayerName}
      totalItemCount={totalItemCount}
      isConfirmEnabled={isConfirmEnabled}
      onChangeText={handleChangeText}
      onConfirm={onConfirm}
      onBack={onBack}
    />
  );
};

TransferReportsConfirmStepContainer.displayName =
  "TransferReportsConfirmStepContainer";

export default TransferReportsConfirmStepContainer;
